/**
 * Send Gift Flow - Add Message
 * =============================
 * Step 4: Add a personal note (optional)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, MessageSquare, Sparkles } from 'lucide-react';

const SendGiftMessagePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category, recipient, amountCents, feeInfo } = location.state || {};
  
  const [isDark, setIsDark] = useState(true);
  const [message, setMessage] = useState('');
  const maxLength = 200;

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!category || !recipient || !amountCents) {
      navigate('/send-gift/select');
    }
  }, [category, recipient, amountCents]);

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37',
    gold: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const suggestedMessages = [
    "Enjoy! 💫",
    "Thinking of you ✨",
    "You deserve this!",
    "Have an amazing time!",
    "Cheers to you! 🥂"
  ];

  const handleContinue = () => {
    navigate('/send-gift/review', {
      state: {
        category,
        recipient,
        amountCents,
        feeInfo,
        message: message.trim()
      }
    });
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '120px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.text
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Send a Gift
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '13px', 
            margin: '4px 0 0 0' 
          }}>
            Step 4 of 5 • Add a note
          </p>
        </div>
      </div>

      {/* Step Progress */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '6px'
        }}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: step <= 4 ? colors.gold : 'rgba(138, 115, 85, 0.2)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Summary */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          background: 'rgba(215, 181, 109, 0.08)',
          borderRadius: '14px',
          border: '1px solid rgba(215, 181, 109, 0.2)'
        }}>
          <span style={{ fontSize: '28px' }}>{category?.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: colors.text, fontSize: '15px', fontWeight: '600' }}>
              {formatCurrency(amountCents)} {category?.name}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '12px' }}>
              For {recipient?.name}
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      <div style={{ padding: '0 20px 24px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '24px',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          Add a note
        </h2>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          Make it personal (optional)
        </p>
      </div>

      {/* Message Input */}
      <div style={{ padding: '0 16px 20px' }}>
        <div style={{
          background: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          padding: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <MessageSquare size={20} color={colors.accent} style={{ marginTop: '4px' }} />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, maxLength))}
              placeholder={`Write something special for ${recipient?.name}...`}
              data-testid="message-input"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontSize: '16px',
                color: colors.text,
                minHeight: '100px',
                lineHeight: '1.5'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '8px'
          }}>
            <span style={{ 
              fontSize: '12px', 
              color: message.length > maxLength * 0.8 ? '#FF9800' : colors.textMuted 
            }}>
              {message.length}/{maxLength}
            </span>
          </div>
        </div>
      </div>

      {/* Suggested Messages */}
      <div style={{ padding: '0 16px 24px' }}>
        <p style={{ 
          color: colors.textMuted, 
          fontSize: '12px', 
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <Sparkles size={14} />
          Quick suggestions
        </p>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          {suggestedMessages.map((msg, idx) => (
            <button
              key={idx}
              onClick={() => setMessage(msg)}
              style={{
                padding: '8px 14px',
                background: message === msg ? 'rgba(215, 181, 109, 0.2)' : colors.cardBg,
                border: message === msg ? '1px solid #D7B56D' : `1px solid ${colors.border}`,
                borderRadius: '20px',
                color: message === msg ? colors.accent : colors.text,
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {msg}
            </button>
          ))}
        </div>
      </div>

      {/* Continue Button */}
      <div style={{
        position: 'fixed',
        bottom: '80px',
        left: 0,
        right: 0,
        padding: '16px',
        background: isDark 
          ? 'linear-gradient(180deg, transparent 0%, #0D0D0D 30%)'
          : 'linear-gradient(180deg, transparent 0%, #FAFAFA 30%)'
      }}>
        <button
          onClick={handleContinue}
          data-testid="continue-btn"
          style={{
            width: '100%',
            padding: '16px',
            background: colors.gold,
            border: 'none',
            borderRadius: '14px',
            color: '#2C1B13',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(215, 181, 109, 0.4)'
          }}
        >
          {message.trim() ? 'Continue' : 'Skip & Continue'}
        </button>
      </div>
    </div>
  );
};

export default SendGiftMessagePage;
